import { useMemo, useState } from "react";
import type { SyntheticEvent } from "react";
import BackToDocumentary from "../../shared/BackToDocumentary/BackToDocumentary";
import { stories } from "../../../content";
import type { Story, StoryStage } from "../../../types/content";

/**
 * ============================================================================
 * Career Stories — the /stories route. Direct consumer of the content layer.
 * ============================================================================
 *
 * No bridge (Constitution V.4): this is a new component with no legacy shape
 * to adapt, so it reads the loader directly, exactly as Platforms does.
 *
 * This is the first section whose unit is the narrative rather than the fact.
 * Certifications, Platforms and Experience all render claims in list form;
 * here each entry carries Situation · Action · Result — the "claim with
 * context, constraints and consequences" that Article I.2 calls documentary
 * material.
 *
 * To change what this page says, edit `src/content/data/stories.json` — not
 * this file.
 */

/**
 * The page's standfirst — the one sentence that says what this route is.
 *
 * A named export rather than inline JSX because `src/seo/routeMeta.ts` uses it
 * verbatim as this route's `<meta name="description">` (D-044). A page
 * description that is literally the page's own opening line cannot drift from
 * what the page says, which is the failure mode hand-written meta descriptions
 * have: the copy is edited and the description is not.
 */
export const STORIES_STANDFIRST =
	"The work behind the resume line — what the situation actually was, what I did about it, and what changed as a result.";

/** Filter state: a category present in the data, or the unfiltered view. */
type Filter = string;

const ALL: Filter = "All";

/**
 * Display labels for the categories in the file today.
 *
 * `Story.category` is a free-form string rather than a closed union (D-019,
 * Decision 3), so this map cannot be exhaustive by construction the way
 * Platforms' can. Everything below therefore falls back rather than assuming:
 * an unmapped category still renders, with its slug title-cased. Content is
 * never dropped for want of a label.
 *
 * "Healthcare (Regulated)" deliberately matches the Platforms label — the same
 * category should not read two different ways on two pages.
 */
const CATEGORY_LABEL: Record<string, string> = {
	"client-leadership": "Client Leadership",
	"healthcare-regulated": "Healthcare (Regulated)",
	transformation: "Transformation",
	"crisis-recovery": "Crisis Recovery",
	governance: "Governance",
	"engineering-governance": "Engineering Governance",
	"product-ownership": "Product Ownership",
	"people-leadership": "People Leadership",
	security: "Security",
	"ai-innovation": "AI & Innovation",
	foundation: "Foundation"
};

/**
 * Accent per category. Whole class strings, because Tailwind's JIT scans
 * source text — a constructed name like `text-${x}-300` is never emitted.
 * Emerald stays on healthcare to match the Platforms page.
 */
const CATEGORY_ACCENT: Record<string, string> = {
	"client-leadership": "border-[var(--cat-sky-border)] text-[var(--cat-sky-text)] bg-[var(--cat-sky-fill)]",
	"healthcare-regulated":
		"border-[var(--cat-emerald-border)] text-[var(--cat-emerald-text)] bg-[var(--cat-emerald-fill)]",
	transformation: "border-[var(--cat-indigo-border)] text-[var(--cat-indigo-text)] bg-[var(--cat-indigo-fill)]",
	"crisis-recovery": "border-[var(--cat-rose-border)] text-[var(--cat-rose-text)] bg-[var(--cat-rose-fill)]",
	governance: "border-[var(--cat-amber-border)] text-[var(--cat-amber-text)] bg-[var(--cat-amber-fill)]",
	"engineering-governance":
		"border-[var(--cat-fuchsia-border)] text-[var(--cat-fuchsia-text)] bg-[var(--cat-fuchsia-fill)]",
	"product-ownership": "border-[var(--cat-violet-border)] text-[var(--cat-violet-text)] bg-[var(--cat-violet-fill)]",
	"people-leadership": "border-[var(--cat-teal-border)] text-[var(--cat-teal-text)] bg-[var(--cat-teal-fill)]",
	security: "border-[var(--cat-red-border)] text-[var(--cat-red-text)] bg-[var(--cat-red-fill)]",
	"ai-innovation": "border-[var(--cat-cyan-border)] text-[var(--cat-cyan-text)] bg-[var(--cat-cyan-fill)]",
	foundation: "border-[var(--cat-stone-border)] text-[var(--cat-stone-text)] bg-[var(--cat-stone-fill)]"
};

const DEFAULT_ACCENT = "border-[var(--cat-slate-border)] text-[var(--cat-slate-text)] bg-[var(--cat-slate-fill)]";

/** "crisis-recovery" → "Crisis Recovery". The fallback when a slug is unmapped. */
const titleCase = (slug: string): string =>
	slug
		.split("-")
		.map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");

/**
 * How an unfinished story announces itself, and what its third block is called.
 *
 * TWO THINGS ARE DERIVED FROM ONE FIELD, deliberately. The pill is the visible
 * signal; the heading is the one that stops the card lying. `NarrativeBlock`
 * has always labelled the third block **"Result"**, which asserts that what
 * follows is what happened — fine for twenty-three stories about completed
 * work, and wrong for an initiative whose third block holds a status and an
 * expectation. D-066 is the precedent: a label beside content, true the day it
 * was written and false on the next entry. Deriving both from `stage` means
 * they cannot drift apart from each other or from the data.
 */
const STAGE_LABEL: Record<StoryStage, string> = {
	proposed: "Proposed — not yet implemented",
	"in-progress": "In progress — not yet delivered"
};

const STAGE_ACCENT = "border-[var(--cat-amber-border)] text-[var(--cat-amber-text)] bg-[var(--cat-amber-fill)]";

/** "Result" only where there is one. */
const outcomeLabel = (stage?: StoryStage): string => (stage === undefined ? "Result" : "Status & Expected Benefit");

export const categoryLabel = (category: string): string => CATEGORY_LABEL[category] ?? titleCase(category);

export const categoryAccent = (category: string): string => CATEGORY_ACCENT[category] ?? DEFAULT_ACCENT;

const MONTHS: Record<string, number> = {
	jan: 1,
	feb: 2,
	mar: 3,
	apr: 4,
	may: 5,
	jun: 6,
	jul: 7,
	aug: 8,
	sep: 9,
	oct: 10,
	nov: 11,
	dec: 12
};

/**
 * Turns a display period into sortable stamps.
 *
 * `period` is deliberately display copy, not a date (schema 1.1.0), so ordering
 * is derived rather than stored — "Feb 2026 – Present", "Dec 2020 – Dec 2024"
 * and "2007 – 2018" all have to sort against each other. Every 4-digit year is
 * read, optionally preceded by a month name; the first is the start and the
 * last is the end. "Present" outranks every real date, which is what puts
 * current work at the top.
 *
 * A period with no year at all yields 0 and sorts last rather than throwing —
 * a malformed string is a content problem, not a reason for a blank page.
 */
const periodStamps = (period: string): { start: number; end: number } => {
	const found: number[] = [];
	const scanner = /(?:([A-Za-z]{3})[a-z]*\s+)?(\d{4})/g;
	let match: RegExpExecArray | null = scanner.exec(period);
	while (match !== null) {
		const month = match[1] ? MONTHS[match[1].toLowerCase()] ?? 1 : 1;
		found.push(Number(match[2]) * 100 + month);
		match = scanner.exec(period);
	}
	const start = found.length ? found[0] : 0;
	const end = /present/i.test(period) ? 999912 : found.length ? found[found.length - 1] : 0;
	return { start, end };
};

/**
 * Newest first, by the end of the period and then by its start. Current work
 * leads; the pre-2018 chapter closes. The JSON keeps its own order — this is a
 * presentation decision, so it lives here (the Platforms precedent).
 */
const orderedStories: Story[] = [...stories].sort((a: Story, b: Story) => {
	const left = periodStamps(a.period);
	const right = periodStamps(b.period);
	return right.end - left.end || right.start - left.start;
});

/**
 * Pill order follows the ordering above — the same newest-first logic — so the
 * pills read in the same direction as the cards. A category with nothing behind
 * it is never offered.
 */
const presentCategories: string[] = orderedStories.reduce((acc: string[], story: Story) => {
	if (!acc.includes(story.category)) acc.push(story.category);
	return acc;
}, []);

const FILTERS: Filter[] = [ALL, ...presentCategories];

const countOf = (filter: Filter): number =>
	filter === ALL ? stories.length : stories.filter((s: Story) => s.category === filter).length;

/** One movement of the narrative. Three of these make a story. */
const NarrativeBlock = ({ label, body }: { label: string; body: string }): JSX.Element => (
	<div>
		<h4 className="text-xs font-semibold uppercase tracking-widest text-[var(--accent-link)]">{label}</h4>
		<p className="mt-2 text-sm leading-relaxed text-[var(--text-secondary)]">{body}</p>
	</div>
);

/**
 * The Situation/Action/Result body collapses by default.
 *
 * At six stories the page could carry every narrative open; at twenty-three it
 * cannot — that is roughly fifteen thousand words, and a reader scanning for
 * the one story that matches their question would have to scroll past all of
 * it. Summary and tags stay visible so the page is scannable; the narrative is
 * one click away.
 *
 * Native `<details>` rather than a hand-rolled disclosure: keyboard support,
 * screen-reader semantics and in-page find all work without any of it being
 * reimplemented. State is held in React only so a filter re-render cannot
 * silently slam an open card shut.
 */
const StoryCard = ({ story, startOpen }: { story: Story; startOpen: boolean }): JSX.Element => {
	const [open, setOpen] = useState(startOpen);

	return (
		// `id` makes the card deep-linkable: the home page's highlight strip links
		// to /stories#<id>, and ScrollManager honours the hash after mount. It
		// stays on the OUTER element with `scroll-mt-24`, so the anchor still
		// lands on the card's own top edge rather than 1px inside it.
		<article id={story.id} className="card-shell scroll-mt-24">
			<div className="card-face flex flex-col p-6">
				<div className="flex flex-wrap items-center gap-2">
					<span className={`pill-tint pill-tint-upper pill-tint-wrap ${categoryAccent(story.category)}`}>
						{categoryLabel(story.category)}
					</span>
					{/* Only ever rendered for unfinished work, so its ABSENCE is not a
					    claim — every card without it is a card about something done. */}
					{story.stage !== undefined && (
						<span className={`pill-tint pill-tint-upper pill-tint-wrap ${STAGE_ACCENT}`}>
							{STAGE_LABEL[story.stage]}
						</span>
					)}
				</div>

				<h3 className="mt-4 text-xl font-bold text-[var(--text-strong)]">{story.title}</h3>

				<p className="mt-2 text-sm text-[var(--text-muted)]">
					{story.organization} · {story.role}
				</p>
				<p className="text-sm text-[var(--text-faint)]">{story.period}</p>

				<p className="mt-4 text-sm font-medium leading-relaxed text-[var(--text-primary)]">{story.summary}</p>

				<details
					open={open}
					onToggle={(event: SyntheticEvent<HTMLDetailsElement>) => setOpen(event.currentTarget.open)}
					className="mt-5 border-t border-[var(--border)] pt-5"
				>
					<summary className="flex min-h-[24px] cursor-pointer list-none items-center text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]">
						{open ? "Hide the full story" : "Read the full story"}
						<span aria-hidden="true" className="ml-2">
							{open ? "↑" : "↓"}
						</span>
					</summary>

					<div className="mt-5 space-y-5">
						<NarrativeBlock label="Situation" body={story.narrative.situation} />
						<NarrativeBlock label="Action" body={story.narrative.action} />
						<NarrativeBlock label={outcomeLabel(story.stage)} body={story.narrative.result} />
					</div>
				</details>

				{/* Pushed to the foot so cards in a row align on their tag rail. */}
				<div className="mt-auto flex flex-wrap gap-2 pt-6">
					{story.tags.map((tag: string) => (
						<span key={tag} className="chip-quiet">
							{tag}
						</span>
					))}
				</div>
			</div>
		</article>
	);
};

const Stories = (): JSX.Element => {
	const [activeFilter, setActiveFilter] = useState<Filter>(ALL);

	/**
	 * A deep-linked story opens itself. Read once at mount rather than from
	 * `useLocation`, because a same-page hash change must not re-open a card the
	 * reader has just closed.
	 */
	const [deepLinked] = useState<string>(() =>
		typeof window === "undefined" ? "" : decodeURIComponent(window.location.hash.slice(1))
	);

	const visible = useMemo(
		() =>
			activeFilter === ALL ? orderedStories : orderedStories.filter((s: Story) => s.category === activeFilter),
		[activeFilter]
	);

	return (
		<section className="section-shell" aria-labelledby="stories-heading">
			<div className="section-rail">
				{/* THE /stories ROUTE'S ONLY <h1> (D-042). It was an <h2> with no h1
				    anywhere above it, so this route shipped a document whose heading
				    outline started at level 2 — an accessibility defect on its own and
				    the reason `document.querySelector("h1")` returned null on both
				    routes. Promoted rather than supplemented: this is already the
				    page's title, and adding a second heading above it would invent
				    copy. */}
				<h1 id="stories-heading" className="heading">
					<span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]">
						Career
					</span>{" "}
					Stories
				</h1>

				<p className="section-standfirst">{STORIES_STANDFIRST}</p>

				<div
					className="mt-8 flex flex-wrap justify-center gap-3"
					role="group"
					aria-label="Filter stories by category"
				>
					{FILTERS.map((filter: Filter) => (
						<button
							key={filter}
							type="button"
							onClick={() => setActiveFilter(filter)}
							aria-pressed={filter === activeFilter}
							className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-200 ${
								filter === activeFilter
									? "border-[var(--accent-marker)] bg-[var(--accent-marker-fill)] text-[var(--text-strong)]"
									: "border-[var(--border-pill)] text-[var(--text-muted)] hover:border-[var(--border-card-hover)] hover:text-[var(--text-bright)]"
							}`}
						>
							{filter === ALL ? ALL : categoryLabel(filter)}
							<span className="ml-2 text-xs opacity-70">{countOf(filter)}</span>
						</button>
					))}
				</div>

				{/* Two columns at xl, not three like Platforms: these cards carry three
				    narrative paragraphs, and prose set in a third of the rail is a
				    column of broken lines. Deliberate divergence, not an oversight. */}
				<div className="mt-12 grid w-full grid-cols-1 gap-6 xl:grid-cols-2 xl:gap-8">
					{visible.map((story: Story) => (
						<StoryCard key={story.id} story={story} startOpen={story.id === deepLinked} />
					))}
				</div>

				<div className="mt-12 text-center">
					<BackToDocumentary />
				</div>
			</div>
		</section>
	);
};

export default Stories;
